import { Link } from 'react-router-dom';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import type { ClientReview } from '../../api/reviews';
import { useClientReviews } from '../../hooks/useReviews';
import { useCurrentSystemUser } from '../../hooks/useSystemUsers';
import { derivedStatus, sortMyActiveReviews } from '../../pages/ReviewsPage';
import { formatPeriodRange } from '../../utils/calendarDate';
import { useTranslation } from '../../i18n';

/**
 * "Next up" on a review you just completed (reviewer proposal #1). Helen works her book in order
 * (positional startedAt 0.005), so finishing one review sends her back to the grid to find the next.
 * This names the next one, in EXACTLY the My Active order the grid uses (`sortMyActiveReviews`:
 * in progress first, then the oldest period), so the link and the grid cannot disagree about what is
 * next.
 */

export interface Me {
  id?: string;
  name?: string;
}

const isMine = (r: Pick<ClientReview, 'supervisorId' | 'supervisorName'>, me: Me) =>
  me.id ? r.supervisorId?.toLowerCase() === me.id.toLowerCase() : !!me.name && r.supervisorName === me.name;

/** The next actionable review in MY queue after `currentId`, or null. PURE. */
export function nextUp(reviews: readonly ClientReview[], me: Me, currentId: string): ClientReview | null {
  const queue = reviews.filter(
    (r) => r.id !== currentId && isMine(r, me) && ['ready', 'in-progress'].includes(derivedStatus(r)),
  );
  return sortMyActiveReviews(queue)[0] ?? null;
}

export function NextUpBanner({ review }: { review: ClientReview }) {
  const { data: currentUser } = useCurrentSystemUser();
  const { data: reviews, isSuccess } = useClientReviews();
  const { t } = useTranslation();
  if (review.status !== 'completed' || !currentUser || !isSuccess) return null;

  const me: Me = { id: currentUser.systemuserid, name: currentUser.fullname };
  // Only on YOUR completed review: someone else's finished review is not where your queue continues.
  if (!isMine(review, me)) return null;

  const next = nextUp(reviews ?? [], me, review.id);
  return (
    <MessageBar intent="success" style={{ marginBottom: '16px' }}>
      <MessageBarBody>
        <MessageBarTitle>{t('nextUp.title')}</MessageBarTitle>
        {next ? (
          <Link to={`/reviews/${next.id}`}>
            {`${next.clientName} · ${formatPeriodRange(next.periodStart, next.periodEnd)}`}
          </Link>
        ) : (
          t('nextUp.none')
        )}
      </MessageBarBody>
    </MessageBar>
  );
}
